import React, { useState, useEffect, useMemo } from 'react';
import {
  VStack,
  Heading,
  Text,
  Box,
  useColorModeValue,
  Image,
  IconButton,
  HStack,
  AspectRatio,
  Wrap,
  Tag,
  TagLabel,
  Badge,
  Tooltip
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon } from '@chakra-ui/icons';

export default function SubLocationInfoWindow({ 
  sublocation, 
  onMediaClick,
  filters = {},
  featuresMetadata = {},
  onFeatureFilter,
  onClose
}) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const textColor = useColorModeValue('gray.800', 'white');
  const bgColor = useColorModeValue('white', 'gray.800');

  // Mobile detection
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);
  
  // Enhanced button styling for better visibility
  const buttonBg = useColorModeValue('rgba(255, 255, 255, 0.95)', 'rgba(26, 32, 44, 0.95)');
  const buttonColor = useColorModeValue('gray.700', 'white');
  const buttonHoverBg = useColorModeValue('white', 'gray.700');
  const buttonBorder = useColorModeValue('gray.200', 'gray.600');

  // Helper function to get filtered media similar to search page
  const getFilteredMedia = useMemo(() => {
    return (allMedia) => {
      if (!Array.isArray(allMedia)) return [];
      
      // Step 1: Filter for valid image media only
      let imageMedia = allMedia.filter(media => {
        if (!media) return false;
        return media.type === 'image' && media.url && media.url.match(/^https?:\/\/.+/);
      });

      // Step 2: If feature filters are active, further filter by features
      if (filters.features && filters.features.length > 0) {
        imageMedia = imageMedia.filter(media => {
          if (!media.features || !Array.isArray(media.features)) return false;
          
          const mediaFeatureIds = media.features.map(String);
          
          if (filters.featureFilterLogic === 'OR') {
            return filters.features.some(selectedFeature => 
              mediaFeatureIds.includes(String(selectedFeature))
            );
          } else { // AND logic
            return filters.features.every(selectedFeature => 
              mediaFeatureIds.includes(String(selectedFeature))
            );
          }
        });
      }
      
      return imageMedia.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    };
  }, [filters.features, filters.featureFilterLogic]);

  // Get all media and filtered media
  const allSublocationMedia = useMemo(() => {
    return (sublocation.media?.filter(item => item.type === 'image') || [])
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  }, [sublocation.media]);
  
  const sublocationMedia = useMemo(() => {
    return getFilteredMedia(sublocation.media || []);
  }, [sublocation.media, getFilteredMedia]);
  
  const hasActiveFilters = filters.features && filters.features.length > 0;
  const showingFiltered = hasActiveFilters && sublocationMedia.length !== allSublocationMedia.length;

  // Reset image index when media changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [sublocationMedia]);

  const handlePrevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex(currentIndex => 
      currentIndex === 0 ? sublocationMedia.length - 1 : currentIndex - 1
    );
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex(currentIndex => 
      currentIndex === sublocationMedia.length - 1 ? 0 : currentIndex + 1
    );
  };

  const currentImage = sublocationMedia[currentImageIndex];
  
  // Helper function to get feature label
  const getFeatureLabel = (featureId) => {
    return featuresMetadata[featureId]?.label || featureId;
  };
  
  // Helper function to get feature category color
  const getFeatureCategoryColor = (featureId) => {
    const category = featuresMetadata[featureId]?.category;
    const categoryColors = {
      'architectural': 'blue',
      'artistic': 'purple',
      'cultural': 'green',
      'material': 'orange',
      'structural': 'red',
      'technological': 'cyan',
      'temporal': 'pink'
    };
    return categoryColors[category] || 'gray';
  };
  
  // Get current image features (support multiple formats) - deduplicated
  const currentImageFeatures = useMemo(() => {
    if (!currentImage) return [];
    
    let features = [];
    
    // New format: features array (from aggregated sublocation data)
    if (currentImage.features && Array.isArray(currentImage.features)) {
      features = currentImage.features;
    }
    // Legacy format: predictions array
    else if (currentImage.predictions && Array.isArray(currentImage.predictions)) {
      features = currentImage.predictions.map(pred => pred.class || pred.id || pred).filter(Boolean);
    }
    
    // Deduplicate features using Set
    return [...new Set(features)];
  }, [currentImage]);
  
  // Get all unique features from this sublocation
  const allSublocationFeatures = useMemo(() => {
    if (sublocation.features && Array.isArray(sublocation.features)) {
      return sublocation.features;
    }
    
    // Fallback: aggregate from all media
    const allFeatures = new Set();
    if (sublocationMedia && Array.isArray(sublocationMedia)) {
      sublocationMedia.forEach(media => {
        if (media.features && Array.isArray(media.features)) {
          media.features.forEach(feature => allFeatures.add(feature));
        }
        // Also include predictions from sublocation
        if (sublocation.predictions && Array.isArray(sublocation.predictions)) {
          sublocation.predictions.forEach(pred => allFeatures.add(pred.class));
        }
      });
    }
    return Array.from(allFeatures);
  }, [sublocation.features, sublocation.predictions, sublocationMedia]);

  return (
    <Box 
      className={isMobile ? 'mobile-popup-container open' : ''}
      p={4} 
      bg={bgColor}
      color={textColor}
      borderRadius={isMobile ? "16px 16px 0 0" : "lg"}
      minW={isMobile ? "100%" : "320px"}
      maxW={isMobile ? "100%" : "400px"}
      boxShadow="lg"
      position={isMobile ? "fixed" : "static"}
      bottom={isMobile ? 0 : "auto"}
      left={isMobile ? 0 : "auto"}
      right={isMobile ? 0 : "auto"}
      zIndex={isMobile ? 1000 : "auto"}
      maxHeight={isMobile ? "70vh" : "auto"}
      overflowY={isMobile ? "auto" : "visible"}
    >
      {isMobile && (
        <>
          <Box className="mobile-popup-handle" />
          <IconButton
            aria-label="Close"
            icon={<CloseIcon />}
            size="sm"
            position="absolute"
            top="12px"
            right="12px"
            zIndex="1"
            onClick={(e) => {
              e.stopPropagation();
              if (onClose) onClose();
            }}
            bg="rgba(0,0,0,0.2)"
            color="white"
            _hover={{ bg: 'rgba(0,0,0,0.4)' }}
          />
        </>
      )}
      <VStack align="start" spacing={3} width="100%">
        <Heading size="sm" color={textColor} noOfLines={1}>
          {sublocation.name}
        </Heading>
        
        {currentImage && (
          <Box position="relative" width="100%" mb={3}>
            <AspectRatio ratio={4/3} width="100%">
              <Box
                position="relative"
                borderRadius="md"
                overflow="hidden"
                bg={useColorModeValue('gray.100', 'gray.700')}
              >
                <Image
                  src={currentImage.url}
                  alt={currentImage.name || 'Sublocation image'}
                  objectFit="cover"
                  width="100%"
                  height="100%"
                  cursor="pointer"
                  onClick={() => {
                    const mediaData = {
                      ...currentImage,
                      // Pass sublocation predictions to enable bounding boxes in the image preview modal
                      sublocationPredictions: sublocation.predictions || [],
                      features: currentImage.features || [],
                      // Also pass the siteId for any operations that need it
                      siteId: sublocation.siteId
                    };
                    if (process.env.NODE_ENV === 'development') {
                      console.log('[SubLocationInfoWindow] =================== DEBUG START ===================');
                      console.log('[SubLocationInfoWindow] Full sublocation object:', sublocation);
                      console.log('[SubLocationInfoWindow] Current image object:', currentImage);
                      console.log('[SubLocationInfoWindow] Media data being passed:', mediaData);
                      console.log('[SubLocationInfoWindow] Sublocation predictions:', sublocation.predictions);
                      console.log('[SubLocationInfoWindow] Sublocation predictions length:', sublocation.predictions?.length || 0);
                      console.log('[SubLocationInfoWindow] Sample prediction:', sublocation.predictions?.[0]);
                      
                      // Check if predictions have coordinates
                      if (sublocation.predictions && sublocation.predictions.length > 0) {
                        sublocation.predictions.forEach((pred, idx) => {
                          console.log(`[SubLocationInfoWindow] Prediction ${idx}:`, {
                            class: pred.class,
                            hasCoordinates: !!(pred.x !== undefined && pred.y !== undefined && pred.width !== undefined && pred.height !== undefined),
                            coordinates: { x: pred.x, y: pred.y, width: pred.width, height: pred.height },
                            fullPrediction: pred
                          });
                        });
                      }
                      console.log('[SubLocationInfoWindow] =================== DEBUG END ===================');
                    }
                    onMediaClick && onMediaClick(mediaData);
                  }}
                  _hover={{ transform: 'scale(1.02)' }}
                  transition="all 0.2s"
                  loading="lazy"
                />
              </Box>
            </AspectRatio>
            
            {sublocationMedia.length > 1 && currentImage && (
              <>
                <IconButton
                  icon={<ChevronLeftIcon boxSize={4} />}
                  position="absolute"
                  left={2}
                  top="50%"
                  transform="translateY(-50%)"
                  onClick={handlePrevImage}
                  bg={buttonBg}
                  color={buttonColor}
                  border="1px solid"
                  borderColor={buttonBorder}
                  _hover={{ 
                    bg: buttonHoverBg,
                    transform: 'translateY(-50%) scale(1.05)',
                    shadow: 'md'
                  }}
                  _active={{
                    transform: 'translateY(-50%) scale(0.95)'
                  }}
                  size="sm"
                  isRound
                  aria-label="Previous image"
                  zIndex={2}
                  transition="all 0.2s"
                />
                <IconButton
                  icon={<ChevronRightIcon boxSize={4} />}
                  position="absolute"
                  right={2}
                  top="50%"
                  transform="translateY(-50%)"
                  onClick={handleNextImage}
                  bg={buttonBg}
                  color={buttonColor}
                  border="1px solid"
                  borderColor={buttonBorder}
                  _hover={{ 
                    bg: buttonHoverBg,
                    transform: 'translateY(-50%) scale(1.05)',
                    shadow: 'md'
                  }}
                  _active={{
                    transform: 'translateY(-50%) scale(0.95)'
                  }}
                  size="sm"
                  isRound
                  aria-label="Next image"
                  zIndex={2}
                  transition="all 0.2s"
                />
                <HStack
                  position="absolute"
                  bottom={2}
                  left="50%"
                  transform="translateX(-50%)"
                  spacing={1}
                  zIndex={2}
                  bg="blackAlpha.300"
                  px={2}
                  py={1}
                  borderRadius="full"
                >
                  {sublocationMedia.map((_, index) => (
                    <Box
                      key={index}
                      w={2}
                      h={2}
                      borderRadius="full"
                      bg={index === currentImageIndex ? "white" : "whiteAlpha.500"}
                      border="1px solid"
                      borderColor={index === currentImageIndex ? "whiteAlpha.800" : "transparent"}
                      transition="all 0.2s"
                      _hover={{
                        transform: 'scale(1.2)'
                      }}
                    />
                  ))}
                </HStack>
              </>
            )}
          </Box>
        )}

        {sublocation.description && (
          <Text fontSize="sm" color={textColor} noOfLines={2}>
            {sublocation.description}
          </Text>
        )}

        <Box width="100%">
          {/* Feature Tags for Current Image */}
          {currentImageFeatures.length > 0 && (
            <Box mb={2}>
              <Text fontSize="xs" color={textColor} opacity={0.7} mb={1}>
                Features:
              </Text>
              <Wrap spacing={1}>
                {currentImageFeatures.map((featureId, idx) => {
                  const isActive = filters.features && filters.features.includes(String(featureId));
                  return (
                    <Tooltip 
                      key={idx} 
                      label={onFeatureFilter ? `Click to ${isActive ? 'remove' : 'add'} filter` : getFeatureLabel(featureId)}
                      placement="top" 
                      hasArrow
                    >
                      <Tag
                        size="sm"
                        variant={isActive ? 'solid' : 'subtle'}
                        colorScheme={getFeatureCategoryColor(featureId)}
                        cursor={onFeatureFilter ? 'pointer' : 'default'}
                        onClick={() => onFeatureFilter && onFeatureFilter({ id: featureId })}
                        _hover={onFeatureFilter ? { transform: 'scale(1.05)' } : {}}
                        transition="all 0.2s"
                      >
                        <TagLabel>{getFeatureLabel(featureId)}</TagLabel>
                      </Tag>
                    </Tooltip>
                  );
                })}
              </Wrap>
            </Box>
          )}
          
          {/* Media Count */}
          <HStack justify="space-between">
            <Text fontSize="sm" color={textColor} opacity={0.8}>
              {showingFiltered ? (
                <>
                  Showing {sublocationMedia.length} of {allSublocationMedia.length} photos
                  <Badge ml={2} colorScheme="blue" size="sm">Filtered</Badge>
                </>
              ) : (
                `${sublocationMedia.length} ${sublocationMedia.length === 1 ? 'photo' : 'photos'}`
              )}
            </Text>
          </HStack>
        </Box>
      </VStack>
    </Box>
  );
}
